import type { SVGProps } from 'react'

import { cn } from '@/lib/utils'

// The app shell's line icons, taken path-for-path from the Figma "Shell" frame (16 × 16 glyphs,
// 1.5px round stroke). They draw in currentColor so each call site colors them with a token
// (rail items use the accent, top-bar tools foreground/60) instead of the file's fixed fills.

type IconProps = SVGProps<SVGSVGElement>

function StrokeIcon({
  className,
  viewBox = '0 0 16 16',
  children,
  ...props
}: IconProps & { viewBox?: string }) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('size-4 shrink-0', className)}
      {...props}
    >
      {children}
    </svg>
  )
}

/** The OrderX mark as the rail draws it: two strokes, not the filled logo. The 2.5px stroke
 *  overhangs the 16px box by its half-width, as in the design. */
export function ShellLogoIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden="true"
      className={cn('size-4 shrink-0 overflow-visible', className)}
      {...props}
    >
      <path d="M0 0L6 8L0 16M16 0L10 8L16 16" />
    </svg>
  )
}

/** Home: a house outline with a door, drawn on the same 16px grid and stroke as the rail set. */
export function HomeIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2.5 7L8 2.5L13.5 7V13.5H2.5V7Z" />
      <path d="M6.5 13.5V10H9.5V13.5" />
    </StrokeIcon>
  )
}

export function CandlesIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M4.5 3V13M11.5 2V14M3 5H6V11H3V5ZM10 4H13V9H10V4Z" />
    </StrokeIcon>
  )
}

export function LayersIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2 8L8 11L14 8M2 11L8 14L14 11M8 2L14 5L8 8L2 5L8 2Z" />
    </StrokeIcon>
  )
}

export function TrendIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2 12L6 8L9 10L14 4M14 8V4H10" />
    </StrokeIcon>
  )
}

export function RadarIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M8 8L12 4M2 8C2 4.6862 4.6862 2 8 2C11.3138 2 14 4.6862 14 8C14 11.3138 11.3138 14 8 14C4.6862 14 2 11.3138 2 8ZM5.5 8C5.5 6.61925 6.61925 5.5 8 5.5C9.38075 5.5 10.5 6.61925 10.5 8C10.5 9.38075 9.38075 10.5 8 10.5C6.61925 10.5 5.5 9.38075 5.5 8Z" />
    </StrokeIcon>
  )
}

export function ArbitrageIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2 5H13M10 8L13 5L10 2M14 11H3M6 14L3 11L6 8" />
    </StrokeIcon>
  )
}

/** RFQ: a request bubble answered by a quote bubble, on the rail's 16px grid and stroke. */
export function RfqIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2 2.5H9.5V7.5H5L2 10V2.5Z" />
      <path d="M11.5 6H14V13.5L11 11H6.5V9.5" />
    </StrokeIcon>
  )
}

export function SparkleIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M14.5 8C10.8 7.4 8.6 5.2 8 1.5C7.4 5.2 5.2 7.4 1.5 8C5.2 8.6 7.4 10.8 8 14.5C8.6 10.8 10.8 8.6 14.5 8Z" />
    </StrokeIcon>
  )
}

/** Settings, drawn as the Figma rail's ring-in-a-ring. */
export function SettingsRingIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.5" />
    </StrokeIcon>
  )
}

export function PagesGridIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M7 2H2V7H7V2Z" />
      <path d="M14 2H9V7H14V2Z" />
      <path d="M7 9H2V14H7V9Z" />
      <path d="M14 9H9V14H14V9Z" />
    </StrokeIcon>
  )
}

/** 12 × 12 chevron next to the pages-switcher glyph. */
export function ChevronSmallIcon({ className, ...props }: IconProps) {
  return (
    <StrokeIcon viewBox="0 0 12 12" className={cn('size-3', className)} {...props}>
      <path d="M2 4L6 8L10 4" />
    </StrokeIcon>
  )
}

/** The icon-button "+" (a 24-unit box with a 10-unit cross). */
export function AddIcon({ className, ...props }: IconProps) {
  return (
    <StrokeIcon viewBox="0 0 24 24" className={cn('size-6', className)} {...props}>
      <path d="M12 7V17M7 12H17" />
    </StrokeIcon>
  )
}

/** The icon-button "×" (a 24-unit box with an 8-unit cross). */
export function CloseIcon({ className, ...props }: IconProps) {
  return (
    <StrokeIcon viewBox="0 0 24 24" className={cn('size-6', className)} {...props}>
      <path d="M8 8L16 16M16 8L8 16" />
    </StrokeIcon>
  )
}

/** The small "×" on a widget's instrument tab: an 8-unit cross with no padding. */
export function TabCloseIcon({ className, ...props }: IconProps) {
  return (
    <StrokeIcon viewBox="0 0 8 8" className={cn('size-2 overflow-visible', className)} {...props}>
      <path d="M0 0L8 8M8 0L0 8" />
    </StrokeIcon>
  )
}

/** Vertical "more" dots (2.5 × 11.5). */
export function MoreDotsIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 2.5 11.5"
      fill="currentColor"
      aria-hidden="true"
      className={cn('h-[11.5px] w-[2.5px] shrink-0', className)}
      {...props}
    >
      <circle cx="1.25" cy="1.25" r="1.25" />
      <circle cx="1.25" cy="5.75" r="1.25" />
      <circle cx="1.25" cy="10.25" r="1.25" />
    </svg>
  )
}

export function KeyboardIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 7H4.01M7 7H7.01M10 7H10.01M5 9.5H11M1.5 4H14.5V12H1.5V4Z" />
    </StrokeIcon>
  )
}

export function SupportIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M14 2.5H2V10.5H4V13.5L7 10.5H14V2.5Z" />
    </StrokeIcon>
  )
}

export function SearchIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M10.25 10.25L13.75 13.75M2.25 6.75C2.25 4.26465 4.26465 2.25 6.75 2.25C9.23535 2.25 11.25 4.26465 11.25 6.75C11.25 9.23535 9.23535 11.25 6.75 11.25C4.26465 11.25 2.25 9.23535 2.25 6.75Z" />
    </StrokeIcon>
  )
}

export function EyeIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M8 3C5.5 3 3 4.5 1 8C3 11.5 5.5 13 8 13C10.5 13 13 11.5 15 8C13 4.5 10.5 3 8 3Z" />
      <circle cx="8" cy="8" r="2" />
    </StrokeIcon>
  )
}

/** EyeIcon struck through, for the "balances hidden" state. */
export function EyeOffIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M8 3C5.5 3 3 4.5 1 8C3 11.5 5.5 13 8 13C10.5 13 13 11.5 15 8C13 4.5 10.5 3 8 3Z" />
      <circle cx="8" cy="8" r="2" />
      <path d="M2 2L14 14" />
    </StrokeIcon>
  )
}

export function BellIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M6.5 13.75H9.5M4 10.25V6.25C4 3.85 5.8 2.25 8 2.25C10.2 2.25 12 3.85 12 6.25V10.25L13.5 11.75H2.5L4 10.25Z" />
    </StrokeIcon>
  )
}

export function FullscreenIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M2 6V2H6M10 2H14V6M14 10V14H10M6 14H2V10" />
    </StrokeIcon>
  )
}

/** FullscreenIcon with its corners turned inward, for "exit fullscreen". */
export function ExitFullscreenIcon(props: IconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M6 2V6H2M14 6H10V2M10 14V10H14M2 10H6V14" />
    </StrokeIcon>
  )
}

/** The status-bar tick arrow (7 × 6 box, a 6.06 × 4.5 triangle). Points up; rotate for down. */
export function TickArrowIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="-0.47 0 7 6"
      fill="currentColor"
      aria-hidden="true"
      className={cn('h-1.5 w-[7px] shrink-0', className)}
      {...props}
    >
      <path d="M3.03109 0L6.06218 4.5H0L3.03109 0Z" />
    </svg>
  )
}
